/**
 * Cost Explorer — tela `2c`.
 *
 * ⚠️ **O frontend NÃO escolhe a fonte de dados.** Existe um endpoint,
 * `POST /finops/custos/consulta`, e o servidor decide entre o agregado no RDS
 * (mensal, dimensões dentro do grão) e o Athena sobre o Parquet do CUR
 * (diário, tag, recurso). A resposta declara a `fonte` para a tela poder MOSTRAR
 * a procedência — mostrar não é o mesmo que decidir.
 *
 * ⚠️ **Group-by MÚLTIPLO**, seguindo o handoff `v3`, que restaurou os chips
 * removíveis. O `PROJECT-OVERVIEW` §3.1/§7.3 ainda diz "single field", citando um
 * "ignore this, por enquanto" de 29/07 — contradição registrada como **Q1**. O
 * `CLAUDE.md` deste repositório é explícito: onde os dois discordam, o handoff
 * vence e o doc é que está errado.
 */
import { z } from 'zod'

import { dataIso } from './comum'

/**
 * As dimensões que o grão da tabela-fato conhece.
 *
 * `tag` e `recurso` NÃO estão aqui: tag é um map de cardinalidade ilimitada
 * definida pelo cliente, e promovê-la a dimensão do agregado significaria
 * explosão combinatória ou DDL a cada tag nova. Consultas por tag são roteadas
 * ao Athena pelo servidor — a tela pede do mesmo jeito.
 */
export const DIMENSOES_DE_CUSTO = [
  'servico',
  'conta',
  'regiao',
  'tipoInstancia',
  'tipoUso',
  'tipoCobranca',
  'opcaoCompra',
] as const

export type DimensaoDeCusto = (typeof DIMENSOES_DE_CUSTO)[number]

export const ROTULO_DA_DIMENSAO: Record<DimensaoDeCusto, string> = {
  servico: 'Serviço',
  conta: 'Conta',
  regiao: 'Região',
  tipoInstancia: 'Tipo de instância',
  tipoUso: 'Tipo de uso',
  tipoCobranca: 'Tipo de cobrança',
  opcaoCompra: 'Opção de compra',
}

/** As três opções de "Exibir custos como" da sidebar de opções avançadas. */
export const MODOS_DE_CUSTO = ['nao_combinado', 'amortizado', 'liquido'] as const
export type ModoDeCusto = (typeof MODOS_DE_CUSTO)[number]

export const ROTULO_DO_MODO: Record<ModoDeCusto, string> = {
  nao_combinado: 'Custos não combinados',
  amortizado: 'Custos amortizados',
  liquido: 'Custos líquidos',
}

export const filtroDeCustoSchema = z.object({
  campo: z.enum(DIMENSOES_DE_CUSTO),
  /** O seletor "somente incluir ▾" / "excluir" de cada campo da sidebar. */
  modo: z.enum(['incluir', 'excluir']),
  valores: z.array(z.string()).min(1),
})

export type FiltroDeCusto = z.infer<typeof filtroDeCustoSchema>

export const consultaDeCustoSchema = z.object({
  idCliente: z.string(),
  periodo: z.object({ de: dataIso, ate: dataIso }),
  granularidade: z.enum(['mensal', 'diario']).default('mensal'),
  /** Múltiplos, na ordem em que os chips aparecem. Máximo 3: acima disso o
   *  gráfico empilhado vira ruído e a tabela explode em linhas. */
  agruparPor: z.array(z.enum(DIMENSOES_DE_CUSTO)).max(3).default([]),
  exibirCustosComo: z.enum(MODOS_DE_CUSTO).default('nao_combinado'),
  filtros: z.array(filtroDeCustoSchema).default([]),
  /** O checkbox "mostrar apenas recursos sem tag". */
  apenasSemTag: z.boolean().default(false),
})

export type ConsultaDeCusto = z.infer<typeof consultaDeCustoSchema>

/**
 * Formato tabular (colunas + linhas) e não uma lista de objetos: a consulta é
 * dinâmica — as colunas dependem do `agruparPor` — e um objeto por linha
 * repetiria as chaves em cada uma das milhares de linhas do payload.
 */
export const respostaDeCustoSchema = z.object({
  /** `rds` = agregado, milissegundos. `athena` = varredura do Parquet. A tela
   *  mostra a procedência; o servidor é quem decide. */
  fonte: z.enum(['rds', 'athena']),
  /** Quando o agregado foi materializado. Dado velho precisa ser VISÍVEL. */
  materializadoEm: z.string().nullable(),
  medida: z.string(),
  colunas: z.array(z.string()),
  linhas: z.array(z.array(z.union([z.string(), z.number()]))),
  moeda: z.string(),
  /** Só quando a fonte é o Athena — quanto a consulta varreu. */
  custoVarreduraBytes: z.number().nullable().optional(),
})

export type RespostaDeCusto = z.infer<typeof respostaDeCustoSchema>

export const dimensoesDisponiveisSchema = z.object({
  campo: z.string(),
  valores: z.array(z.string()),
})

export type DimensoesDisponiveis = z.infer<typeof dimensoesDisponiveisSchema>
